import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ArchivePost {
  slug: string;
  title: string;
}

/**
 * Crawlable archive of ALL blog posts.
 * Renders every published post as a plain <a> link so search engine crawlers
 * can discover pages that live beyond the paginated blog listing.
 */
const BlogArchiveLinks = () => {
  const { data: posts } = useQuery<ArchivePost[]>({
    queryKey: ['blog-archive-links'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('slug, title')
        .or(`status.eq.published,and(status.eq.scheduled,scheduled_publish_at.lte.${new Date().toISOString()})`)
        .order('title', { ascending: true });

      if (error) throw error;
      return (data || []) as ArchivePost[];
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  if (!posts || posts.length === 0) return null;

  return (
    <section className="mt-20 border-t pt-12">
      <h2 className="text-2xl font-bold text-foreground mb-2">Browse All Articles</h2>
      <p className="text-muted-foreground mb-6">
        All {posts.length} articles on healthy aging, home safety, wellness, and family caregiving.
      </p>
      <ul className="columns-1 sm:columns-2 lg:columns-3 gap-x-8 text-sm">
        {posts.map((post) => (
          <li key={post.slug} className="mb-1.5 break-inside-avoid">
            <Link
              to={`/blog/${post.slug}`}
              className="text-muted-foreground hover:text-primary hover:underline transition-colors"
            >
              {post.title}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default BlogArchiveLinks;
